// src/components/modals/ProgramDescriptionModal.jsx
//
// Triggered from the program card's "הצג תיאור" button — shows the full
// description in a small popup instead of expanding inline in the card.
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

export default function ProgramDescriptionModal({ isOpen, onClose, program }) {
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !program) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center px-0 sm:px-4"
      aria-modal="true"
      role="dialog"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-[3px]" onClick={onClose} />

      <div
        className="relative z-[1] w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/20 flex flex-col max-h-[85dvh] overflow-hidden"
        dir="rtl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 px-5 sm:px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M9 12h6m-6 4h6M8.5 4H16a2 2 0 012 2v12a2 2 0 01-2 2H8a2 2 0 01-2-2V6.5L8.5 4z" />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-gray-800 font-bold text-base leading-tight">תיאור התוכנית</h2>
              <p className="text-gray-500 text-xs mt-0.5 truncate">{program.name}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
            aria-label="סגור"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="px-5 sm:px-6 py-5 overflow-y-auto">
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
            {program.description}
          </p>
        </div>

        <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm px-5 sm:px-6 py-4 flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-6 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all duration-150"
          >
            סגור
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}